// Session AV, Item 3: UK postcode -> address autocomplete. A shared control for the office address
// forms (Site, Project client/site card). It renders the postcode input; when the office types a
// postcode a dropdown of matching addresses appears (via the server-proxied Google Places lookup), and
// selecting one fills the address. It NEVER blocks manual entry: the input is always editable, a failed
// lookup shows a plain message, and with no API key configured it is just a plain input (no dropdown).
import { useEffect, useRef, useState, type KeyboardEvent } from "react";
import { api, type AddressPrediction } from "../lib/api";

const MIN_CHARS = 5; // a UK postcode is at least this long; only then do we look up
const DEBOUNCE_MS = 300;

interface Props {
  postcode: string;
  onPostcodeChange: (value: string) => void;
  onSelect: (result: { address: string; postcode: string }) => void;
  id?: string;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

export function PostcodeLookup({ postcode, onPostcodeChange, onSelect, id, className, placeholder = "Postcode", disabled }: Props) {
  const [results, setResults] = useState<AddressPrediction[]>([]);
  const [available, setAvailable] = useState<boolean | null>(null); // is the Google Maps key configured?
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [resolving, setResolving] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const containerRef = useRef<HTMLDivElement>(null);
  // Once we learn the lookup is unavailable (no key), skip every further fetch so the field is a plain
  // input. A ref (not the deps) avoids a redundant second fetch when `available` flips.
  const unavailableRef = useRef(false);

  const trimmed = postcode.trim();

  // Debounced autocomplete: only at/above the minimum length, and never when the lookup is unavailable.
  useEffect(() => {
    if (unavailableRef.current) return;
    if (trimmed.length < MIN_CHARS) {
      setResults([]);
      setSearched(false);
      setLoading(false);
      return;
    }
    let live = true;
    setLoading(true);
    const t = setTimeout(() => {
      api.addressLookup
        .search(trimmed)
        .then((r) => {
          if (!live) return;
          if (!r.configured) unavailableRef.current = true;
          setAvailable(r.configured);
          setResults(r.configured ? r.results : []);
          setSearched(true);
          setLoading(false);
        })
        .catch(() => {
          if (!live) return;
          setResults([]);
          setSearched(true);
          setLoading(false);
        });
    }, DEBOUNCE_MS);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [trimmed]);

  useEffect(() => setActiveIndex(-1), [trimmed]);

  // Click outside closes the dropdown.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    window.addEventListener("mousedown", onDown);
    return () => window.removeEventListener("mousedown", onDown);
  }, []);

  async function choose(p: AddressPrediction) {
    setOpen(false);
    setResolving(true);
    try {
      const details = await api.addressLookup.details(p.id);
      onSelect(details);
    } catch {
      // Could not resolve the full address: use the prediction text and keep the typed postcode.
      onSelect({ address: p.label, postcode: trimmed });
    } finally {
      setResolving(false);
    }
  }

  const lookupOn = available !== false;
  const showDropdown = open && lookupOn && trimmed.length >= MIN_CHARS && (results.length > 0 || (searched && !loading));
  const noResults = showDropdown && searched && !loading && results.length === 0;

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "Escape") {
      setOpen(false);
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setOpen(true);
      setActiveIndex((i) => Math.min(i + 1, results.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActiveIndex((i) => Math.max(i - 1, 0));
    } else if (e.key === "Enter" && activeIndex >= 0 && results[activeIndex]) {
      e.preventDefault();
      void choose(results[activeIndex]!);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <input
        id={id}
        type="text"
        value={postcode}
        disabled={disabled}
        onChange={(e) => {
          onPostcodeChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        placeholder={placeholder}
        role="combobox"
        aria-expanded={showDropdown}
        aria-autocomplete="list"
        aria-label="Postcode (start typing to look up the address)"
        autoComplete="off"
        autoCorrect="off"
        autoCapitalize="characters"
        spellCheck={false}
        data-1p-ignore
        data-lpignore="true"
        className={className}
      />
      {resolving ? <p className="mt-1 text-xs text-ele-body opacity-70">Loading address...</p> : null}
      {showDropdown ? (
        <div role="listbox" className="ele-table-scroll absolute left-0 right-0 z-30 mt-1 max-h-64 overflow-y-auto rounded-ele border border-ele-purple-accent bg-[var(--ele-shell)] shadow-xl">
          {loading ? <div className="px-3 py-2 text-xs text-ele-body opacity-70">Looking up addresses...</div> : null}
          {results.map((p, i) => (
            <button
              key={p.id}
              type="button"
              role="option"
              aria-selected={i === activeIndex}
              onMouseEnter={() => setActiveIndex(i)}
              onClick={() => void choose(p)}
              className={`block w-full px-3 py-1.5 text-left text-sm transition ${i === activeIndex ? "bg-ele-inset-strong text-ele-green" : "text-ele-strong hover:bg-ele-row-hover"}`}
            >
              {p.label}
            </button>
          ))}
          {noResults ? <div className="px-3 py-2 text-sm text-ele-body">No addresses found for this postcode. Enter it manually.</div> : null}
        </div>
      ) : null}
    </div>
  );
}
